import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatLKR } from "@/lib/api";
import { ArrowRight, RefreshCcw, Sparkles, AlertTriangle } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import ReturnProjectionPanel from "./ReturnProjectionPanel";
import BulkPurchaseFlow from "./BulkPurchaseFlow";
import SuggestionSummaryPanel from "./SuggestionSummaryPanel";

const PIE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

const PortfolioSuggestion = ({ portfolio, onContinue, onRegenerate }) => {
  const sug = portfolio?.ai_suggestion;
  if (!sug) {
    return (
      <Card className="border bg-card">
        <CardContent className="py-10 text-center text-sm text-muted-foreground space-y-3">
          <AlertTriangle className="h-6 w-6 text-[hsl(var(--warning))] mx-auto" />
          <div>No AI suggestion was generated. {portfolio?.ai_suggestion_error}</div>
          <Button onClick={onRegenerate} className="gap-2" data-testid="portfolio-regenerate-button">
            <RefreshCcw className="h-4 w-4" /> Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const sectorData = (sug.sector_breakdown || []).map((s, i) => ({
    name: s.sector,
    value: s.allocation_pct,
    valueLkr: s.allocation_lkr,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <div className="space-y-5" data-testid="portfolio-suggestion">
      <Card className="border bg-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Claude’s portfolio thesis</div>
              <p className="mt-1 text-sm leading-relaxed">{sug.rationale}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onRegenerate} className="gap-2" data-testid="portfolio-regenerate-button">
                <RefreshCcw className="h-3.5 w-3.5" /> Regenerate
              </Button>
              <Button size="sm" onClick={onContinue} className="gap-2" data-testid="portfolio-continue-button">
                Continue to dashboard <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Allocations" value={`${(sug.allocations || []).length} positions`} />
        <Stat label="Expected annual dividend" value={formatLKR(sug.expected_annual_dividend_total_lkr)} />
        <Stat label="Portfolio yield" value={`${(sug.portfolio_dividend_yield_pct || 0).toFixed(2)}%`} />
      </div>

      {/* Phase 9 — Portfolio Analysis Summary panel + Phase 7 macro/geo */}
      <SuggestionSummaryPanel
        summary={sug.summary}
        investmentAmount={portfolio?.investment_amount}
        title="Portfolio Analysis Summary"
        macroGeo={sug.macro_geo_summary}
        warnings={sug.macro_geo_warnings || []}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
        <Card className="border bg-card overflow-hidden">
          <CardHeader>
            <CardTitle className="font-display">Sector mix</CardTitle>
            <p className="text-xs text-muted-foreground">
              How Claude has spread your investment across sectors. The
              detailed stock-level list is below — tick or untick to
              customise.
            </p>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 280 }}>
              {sectorData.length === 0 ? (
                <div className="text-sm text-muted-foreground">No data.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280} key={`pie-${sectorData.length}`}>
                  <PieChart>
                    <Pie data={sectorData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2} isAnimationActive={false}>
                      {sectorData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v, _n, item) => [`${v.toFixed(1)}%  (${formatLKR(item.payload.valueLkr)})`, item.payload.name]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border bg-card">
          <CardHeader>
            <CardTitle className="font-display">How this works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-3 leading-relaxed">
            <p>
              Claude built this allocation using your risk profile, the
              latest CSE fundamentals and our 6-layer composite score.
              Higher-scoring stocks get a larger share of your investment.
            </p>
            <p>
              <span className="font-medium text-foreground">Customise:</span>{" "}
              tick or untick stocks in the list below — the LKR is
              instantly redistributed across your selection.
            </p>
            <p>
              <span className="font-medium text-foreground">Net dividends:</span>{" "}
              all yield figures account for the 10% Sri Lankan dividend
              withholding tax (WHT). There is no capital gains tax on CSE
              listed shares.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border bg-card">
        <CardContent className="p-4 text-xs text-muted-foreground">
          This is a Claude-generated suggestion based on your preferences and current CSE fundamentals.
          Tick or untick stocks below to customise your purchases — amounts redistribute automatically.
        </CardContent>
      </Card>

      {/* Phase 7 Stage 9 Part A — Checkbox-based selection + batch record */}
      {(sug.allocations || []).length > 0 && (
        <BulkPurchaseFlow
          portfolioId={portfolio.portfolio_id}
          portfolioName={portfolio.portfolio_name}
          totalAmount={portfolio.investment_amount || sug.total_investment_lkr || 0}
          allocations={(sug.allocations || []).map((a) => ({
            cse_symbol: a.cse_symbol,
            company_code: a.company_code,
            company_name: a.company_name,
            sector: a.sector,
            score: a.score,
            composite_score: a.composite_score ?? a.score,
            composite_signal: a.composite_signal,
            data_confidence: a.data_confidence,
            sector_verdict: a.sector_verdict,
            annual_reports: a.annual_reports,
            interim_reports: a.interim_reports,
            shares: a.shares,
            close_price: a.close_price,
            allocation_lkr: a.allocation_lkr,
            allocation_pct: a.allocation_pct,
            expected_annual_dividend_lkr: a.expected_annual_dividend_lkr,
            reason: a.reason,
            share_type: a.share_type,
            signal_changed: a.signal_changed,
            current_composite_signal: a.current_composite_signal,
            current_composite_score: a.current_composite_score,
            // Test 4 Fix 4 — pass through target price metrics for cards
            target_pe_based: a.target_pe_based,
            target_average: a.target_average,
            target_upside_pct: a.target_upside_pct,
            // Test 6 Fix 1 — canonical base case (matches screener)
            target_base_case: a.target_base_case,
            target_base_case_upside_pct: a.target_base_case_upside_pct,
          }))}
          onCompleted={() => onContinue?.()}
          ctaLabel="Record all purchases"
          testIdPrefix="suggestion-bulk"
          // Phase 9 Fix 5 — distinct fromLabel so the Stock Detail back
          // button can route back to /portfolio?view=suggestion (and
          // PortfolioPage restores the suggestion view + checkbox state).
          fromLabel="Portfolio Suggestion"
        />
      )}

      {/* Phase 7 Stage 7 — Projected Returns for the suggested allocation */}
      <ReturnProjectionPanel
        suggestion={sug}
        initialReinvest={false}
        initialYears={5}
        testIdPrefix="suggestion-projection"
      />
    </div>
  );
};

const Stat = ({ label, value }) => (
  <Card className="border bg-card">
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-display font-semibold tabular">{value}</div>
    </CardContent>
  </Card>
);

export default PortfolioSuggestion;
